import React, { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import MicroReportsPanel from './MicroReportsPanel';
import DocumentViewer, { InsightFrame } from './DocumentViewer';
import RightPanel from './RightPanel';
import type { MicroReport, StepStatusItem, FrameCatalogResponse, ReportSectionsResponse } from '../services/api';

type PendingGenerationMode = 'with_additional_frames' | 'without_additional_frames';

interface WorkspaceViewProps {
  // Loading and state
  loading: boolean;
  microreports: MicroReport[];
  selectedReport: MicroReport | null;
  error: string | null;
  isAnalysisGenerationBusy: boolean;
  isChatBusy: boolean;
  credits: number;
  isSignedIn: boolean;
  
  // Session and journey
  sessionId: string | null;
  journeyStarted: boolean;
  stepStatuses: StepStatusItem[];
  stepStage: string | null;
  finalRegenerating: boolean;
  journeyUnlocked: boolean;
  
  // Reports
  reportSections: ReportSectionsResponse | null;
  augmentationWorkerRunning: boolean;
  augmentationStatusMessage: string | null;
  analysisQueuePending: boolean;
  customReportsEnabled: boolean;
  customReports: MicroReport[];
  
  // Frames
  selectedFrameNumbers: number[];
  completedFrames: number[];
  frameGroups: FrameCatalogResponse | null;
  hasFrameBasedReports: boolean;
  isMarketResearch?: boolean;
  agentId?: string | null;
  briefId?: string | null;
  workspaceName?: string;
  
  // Scratchpad and categories
  scratchpadItems: any[];
  categories: string[];
  rightPanelWidth: number;
  
  // UI state
  showFirstTimeTour: boolean;
  firstTimeTourOptOut: boolean;
  tourStep: 'intro' | 'microreports' | 'documentviewer' | 'rightpanel' | 'scratchpad' | null;
  showSessionNameModal: boolean;
  sessionNameInput: string;
  savingSessionName: boolean;
  isResizing: boolean;
  
  // Credit confirmation modal
  showCreditConfirmationModal: boolean;
  pendingFrameSelection: InsightFrame[] | null;
  pendingGenerationMode: PendingGenerationMode | null;
  presetAnalysesEstimate: number;
  generationEstimateStatus: 'ready' | 'updating' | 'not_ready' | 'already_complete' | null;
  generationEstimateMessage: string | null;
  requiredCredits: number | null;
  isEstimatingGenerationCredits: boolean;
  generationEstimateError: string | null;
  onProceedWithFrameSelection: () => void;
  onCancelFrameSelection: () => void;
  onKillGeneration: () => void;
  isKillingGeneration: boolean;

  // Workspace closing
  isClosingWorkspace?: boolean;
  
  // Refs
  layoutRef: React.RefObject<HTMLDivElement | null>;
  
  // Handlers
  onSelectReport: (report: MicroReport) => void;
  onAddToScratchpad: (item: any, category?: string) => void;
  onAddCategory: (name: string) => void;
  onRemoveScratchpadItem: (id: number) => void;
  onChatMessage: (message: string, options?: { onToken?: (chunk: string) => void }) => Promise<any>;
  onGenerateFraming: (goal: string) => Promise<any>;
  onCanvasCategoriesChange: (names: string[]) => void;
  onCloseWorkspace: () => Promise<void>;
  onCreateUserReport: () => void;
  onUpdateCustomReport?: (id: string, updates: Partial<MicroReport>) => void;
  onDeleteCustomReport?: (id: string) => void;
  onToggleFrame: (frameNumber: number, frame: InsightFrame) => void;
  onSubmitFrameSelection: (frames: InsightFrame[], autoRun?: boolean) => void;
  onClearFrameSelection: () => void;
  onSkipFrameSelection: () => void;
  onFramesParsed: (frames: InsightFrame[], reportId: string | undefined) => void;
  onSaveSessionName: () => Promise<void>;
  onSetSessionNameInput: (value: string) => void;
  onSetIsResizing: (resizing: boolean) => void;
  onDismissFirstTimeTour: () => void;
  onSetFirstTimeTourOptOut: (optOut: boolean) => void;
  onShowFirstTimeTour?: () => void;
  onTourStepComplete: () => void;
  onShowTourStep?: (step: 'microreports' | 'documentviewer' | 'rightpanel' | 'scratchpad') => void;
}

const WorkspaceView: React.FC<WorkspaceViewProps> = ({
  loading,
  microreports,
  selectedReport,
  error,
  isAnalysisGenerationBusy,
  isChatBusy,
  credits,
  isSignedIn,
  sessionId,
  journeyStarted,
  stepStatuses,
  stepStage,
  finalRegenerating,
  journeyUnlocked,
  reportSections,
  augmentationWorkerRunning,
  augmentationStatusMessage,
  analysisQueuePending,
  customReportsEnabled,
  customReports,
  selectedFrameNumbers,
  completedFrames,
  frameGroups,
  hasFrameBasedReports,
  isMarketResearch = false,
  agentId = null,
  briefId = null,
  workspaceName = '',
  scratchpadItems,
  categories,
  rightPanelWidth,
  showFirstTimeTour,
  firstTimeTourOptOut,
  tourStep,
  showSessionNameModal,
  sessionNameInput,
  savingSessionName,
  isResizing,
  showCreditConfirmationModal,
  pendingFrameSelection,
  pendingGenerationMode,
  presetAnalysesEstimate,
  generationEstimateStatus,
  generationEstimateMessage,
  requiredCredits,
  isEstimatingGenerationCredits,
  generationEstimateError,
  onProceedWithFrameSelection,
  onCancelFrameSelection,
  onKillGeneration,
  isKillingGeneration,
  layoutRef,
  onSelectReport,
  onAddToScratchpad,
  onAddCategory,
  onRemoveScratchpadItem,
  onChatMessage,
  onGenerateFraming,
  onCanvasCategoriesChange,
  onCloseWorkspace,
  onCreateUserReport,
  onUpdateCustomReport,
  onDeleteCustomReport,
  onToggleFrame,
  onSubmitFrameSelection,
  onClearFrameSelection,
  onSkipFrameSelection,
  onFramesParsed,
  onSaveSessionName,
  onSetSessionNameInput,
  onSetIsResizing,
  onDismissFirstTimeTour,
  onSetFirstTimeTourOptOut,
  onShowFirstTimeTour,
  onTourStepComplete,
  onShowTourStep,
  isClosingWorkspace = false,
}) => {
  // Don't blur workspace shell - session name modal and first-time tour popup use CSS to blur only the document viewer
  const workspaceShellClassName = 'workspace-shell';

  const microreportIdMap = useMemo(() => {
    const map = new Map<string, string>();
    (stepStatuses || []).forEach((step) => {
      const microId = (step as any).id;
      const reportId = (step as any).report_id;
      if (microId && reportId) {
        map.set(String(microId), String(reportId));
      }
    });
    const usedReportIds = new Set(Array.from(map.values()));
    const stepReports = microreports.filter((report) => (report.type || '').toLowerCase() === 'step');
    (reportSections?.microreports || []).forEach((microreport, idx) => {
      const microId = String(microreport.id || `microreport${idx + 1}`).trim();
      const microreportName = (microreport.name || '').trim().toLowerCase();
      if (!microId || map.has(microId) || !microreportName) {
        return;
      }
      const matchedReport = stepReports.find((report) => {
        if (usedReportIds.has(report.id)) {
          return false;
        }
        const reportTitle = (report.title || '').trim().toLowerCase();
        return (
          reportTitle === microreportName ||
          reportTitle.includes(microreportName) ||
          microreportName.includes(reportTitle)
        );
      });
      if (!matchedReport) {
        return;
      }
      map.set(microId, matchedReport.id);
      usedReportIds.add(matchedReport.id);
    });
    return map;
  }, [microreports, reportSections, stepStatuses]);

  const hasGeneratedPresetAnalyses = useMemo(() => {
    return microreports.some((report) => {
      const reportType = (report.type || '').toLowerCase();
      return reportType === 'step' || reportType === 'final';
    });
  }, [microreports]);


  return (
    <>
      <div className={workspaceShellClassName}>
        {/* Tour overlay - covers Document Viewer and Right Panel only */}
        {tourStep === 'microreports' && (
          <>
            {/* Blurred overlay for Document Viewer (center) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: '256px', // Start after micro reports panel (w-64)
                right: `${rightPanelWidth}px`, // End before right panel
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, .8)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Blurred overlay for Right Panel */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: `${rightPanelWidth}px`,
                bottom: 0,
                backgroundColor: 'rgba(20, 19, 19, 0.7)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Highlight box for micro reports panel */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '256px', // w-64 = 256px
                height: '100vh',
                border: '3px solid rgba(6, 182, 212, 0.8)',
                borderRadius: '0.5rem',
                boxShadow: '0 0 40px rgba(6, 182, 212, 0.6), inset 0 0 40px rgba(6, 182, 212, 0.1)',
                backgroundColor: 'rgba(6, 182, 212, 0.05)',
                pointerEvents: 'none',
                zIndex: 998,
              }}
            />
            {/* Message tooltip - separate layer, not affected by blur */}
            <div
              style={{
                position: 'fixed',
                top: '43%',
                left: '220px',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.75rem',
                padding: '1.25rem 1.5rem',
                maxWidth: '320px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                pointerEvents: 'auto',
                zIndex: 1000,
              }}
            >
              <div style={{ color: '#e2e8f0', lineHeight: '1.7', fontSize: '0.9375rem' }}>
                <p style={{ margin: 0, marginBottom: '1rem' }}>
                  The left side of your workspace is your analysis navigator.
                </p>
                <p style={{ margin: 0, marginBottom: '.3rem' }}>
                  To launch your analysis you will 
                </p>
                <p style={{ marginLeft: '1rem', marginBottom: '.3rem' }}>
                 1. Click competitive frames. 
                </p>
                <p style={{ marginLeft: '1rem', marginBottom: '1.5rem' }}>
                 2. Click generate analyses 
                </p>
                <p style={{ margin: 0, marginBottom: '0.75rem' }}>
                   <strong style={{ color: '#06b6d4' }}>Pro tip:</strong> astra breaks your scope into smaller detailed analyses called microanalyses.
                </p>
              </div>
              <button
                onClick={onTourStepComplete}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#06b6d4',
                  border: '1px solid rgba(6, 182, 212, 0.5)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '400',
                  fontSize: '0.75rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(6, 182, 212, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                }}
              >
                <span>proceed</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
        {/* Tour overlay for Document Viewer */}
        {tourStep === 'documentviewer' && (
          <>
            {/* Blurred overlay for Micro Reports Panel (left) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '256px', // w-64 = 256px
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Blurred overlay for Right Panel */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: `${rightPanelWidth}px`,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Highlight box for Document Viewer (center) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: '256px', // Start after micro reports panel
                right: `${rightPanelWidth}px`, // End before right panel
                bottom: 0,
                backdropFilter: 'blur(2px)',
                border: '3px solid rgba(6, 182, 212, 0.8)',
                borderRadius: '0.5rem',
                boxShadow: '0 0 40px rgba(6, 182, 212, 0.6), inset 0 0 40px rgba(6, 182, 212, 0.1)',
                backgroundColor: 'rgba(4, 8, 9, 0.65)',
                pointerEvents: 'none',
                zIndex: 998,
              }}
            />
            {/* Message tooltip - separate layer, not affected by blur */}
            <div
              style={{
                position: 'fixed',
                top: '43%',
                left: `calc(256px + (100% - 256px - ${rightPanelWidth}px) / 2)`,
                transform: 'translateX(-50%) translateY(-50%)',
                backgroundColor: 'rgba(1, 2, 3, 0.4)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(2px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.75rem',
                padding: '1.25rem 1.5rem',
                maxWidth: '400px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                pointerEvents: 'auto',
                zIndex: 1000,
              }}
            >
              <div style={{ color: '#e2e8f0', lineHeight: '1.7', fontSize: '0.9375rem' }}>
                <p style={{ margin: 0, marginBottom: '.75rem' }}>
                   The center of your workspace displays the analysis contents and any key drawings.
                </p>
                <p style={{ margin: 0, marginBottom: '0.75rem' }}>
                   <strong style={{ color: '#06b6d4' }}>Pro tip:</strong> You can copy content from analyses and add them to your scratchpad to organize your thoughts.
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  To copy to scratchpad: Press <kbd style={{ backgroundColor: 'rgba(6, 182, 212, 0.2)', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.8125rem' }}>S</kbd> + select the content to be copied.
                </p>
              </div>
              <button
                onClick={onTourStepComplete}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#06b6d4',
                  border: '1px solid rgba(6, 182, 212, 0.5)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '400',
                  fontSize: '0.75rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(6, 182, 212, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                }}
              >
                <span>proceed</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
        {/* Tour overlay for Right Panel */}
        {tourStep === 'rightpanel' && (
          <>
            {/* Blurred overlay for Micro Reports Panel (left) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '256px', // w-64 = 256px
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Blurred overlay for Document Viewer (center) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: '256px', // Start after micro reports panel
                right: `${rightPanelWidth}px`, // End before right panel
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, .8)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Highlight box for Right Panel */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: `${rightPanelWidth}px`,
                bottom: 0,
                border: '3px solid rgba(6, 182, 212, 0.8)',
                borderRadius: '0.5rem',
                boxShadow: '0 0 40px rgba(6, 182, 212, 0.6), inset 0 0 40px rgba(6, 182, 212, 0.1)',
                backgroundColor: 'rgba(34, 197, 94, 0.08)',
                pointerEvents: 'none',
                zIndex: 998,
              }}
            />
            {/* Message tooltip - separate layer, not affected by blur */}
            <div
              style={{
                position: 'fixed',
                top: '50%',
                right: `calc(${rightPanelWidth}px - 75px)`,
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.75rem',
                padding: '1.25rem 1.5rem',
                maxWidth: '320px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                pointerEvents: 'auto',
                zIndex: 1000,
              }}
            >
              <div style={{ color: '#e2e8f0', lineHeight: '1.7', fontSize: '0.9375rem' }}>
                <p style={{ margin: 0, marginBottom: '0.75rem' }}>
                  This is where you can chat with me, <strong style={{ color: '#06b6d4' }}>astra</strong>.
                </p>
                <p style={{ margin: 0, marginBottom: '0.75rem' }}>
                   Ask me questions about contents in the analysis, find additional information not in the analyses, brainstorm strategy, and more.
                </p>
              </div>
              <button
                onClick={onTourStepComplete}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#06b6d4',
                  border: '1px solid rgba(6, 182, 212, 0.5)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '400',
                  fontSize: '0.75rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(6, 182, 212, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                }}
              >
                <span>proceed</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
        {/* Tour overlay for Scratchpad */}
        {tourStep === 'scratchpad' && (
          <>
            {/* Blurred overlay for Micro Reports Panel (left) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '256px', // w-64 = 256px
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Blurred overlay for Document Viewer (center) */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: '256px', // Start after micro reports panel
                right: `${rightPanelWidth}px`, // End before right panel
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, .8)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Blurred overlay for Chat tab in Right Panel */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: `${rightPanelWidth}px`,
                height: '60px', // Approximate height of tab area
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                zIndex: 999,
                pointerEvents: 'auto',
              }}
            />
            {/* Highlight box for Scratchpad section in Right Panel */}
            <div
              style={{
                position: 'fixed',
                top: '60px', // Start below tab area
                right: 0,
                width: `${rightPanelWidth}px`,
                bottom: 0,
                border: '3px solid rgba(6, 182, 212, 0.8)',
                borderTop: 'none',
                borderRadius: '0 0 0.5rem 0.5rem',
                boxShadow: '0 0 40px rgba(6, 182, 212, 0.6), inset 0 0 40px rgba(6, 182, 212, 0.1)',
                backgroundColor: 'rgba(6, 182, 212, 0.05)',
                pointerEvents: 'none',
                zIndex: 998,
              }}
            />
            {/* Message tooltip - separate layer, not affected by blur */}
            <div
              style={{
                position: 'fixed',
                top: '85px',
                right: `calc(${rightPanelWidth}px + 140px)`,
                transform: 'translateX(100%)',
                backgroundColor: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.75rem',
                padding: '1.25rem 1.5rem',
                maxWidth: '320px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                pointerEvents: 'auto',
                zIndex: 1000,
              }}
            >
              <div style={{ color: '#e2e8f0', lineHeight: '1.7', fontSize: '0.9375rem' }}>
                <p style={{ margin: 0, marginBottom: '0.75rem' }}>
                  This is your <strong style={{ color: '#06b6d4' }}>Analysis Scratchpad</strong>.
                </p>
                <p style={{ margin: 0 }}>
                  Press <kbd style={{ backgroundColor: 'rgba(6, 182, 212, 0.2)', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.8125rem' }}>S</kbd> + select to copy and find your content here. Oganize thoughts, ideas, and key insights.
                </p>
              </div>
              <button
                onClick={onTourStepComplete}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#06b6d4',
                  border: '1px solid rgba(6, 182, 212, 0.5)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '400',
                  fontSize: '0.75rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(6, 182, 212, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                }}
              >
                <span>This is the end of your tour</span>
              </button>
            </div>
          </>
        )}
        <div ref={layoutRef} className="flex h-screen bg-gray-900 text-white overflow-hidden">
          <MicroReportsPanel
            reports={microreports}
            selectedReport={selectedReport}
            onSelectReport={onSelectReport}
            credits={credits}
            isSignedIn={isSignedIn}
            loading={loading}
            error={error}
            stepStatuses={stepStatuses}
            sessionId={sessionId}
            hasSession={journeyStarted || !!sessionId}
            isBusy={isAnalysisGenerationBusy}
            stepStage={stepStage}
            finalRegenerating={finalRegenerating}
            journeyUnlocked={journeyUnlocked}
            customReportsEnabled={customReportsEnabled}
            userReports={customReports}
            onCreateUserReport={onCreateUserReport}
            reportSections={reportSections}
            augmentationWorkerRunning={augmentationWorkerRunning}
            onKillGeneration={onKillGeneration}
            isKillingGeneration={isKillingGeneration}
            briefId={briefId}
            workspaceName={workspaceName}
          />

          <DocumentViewer
            report={selectedReport}
            onAddToScratchpad={onAddToScratchpad}
            isBusy={isAnalysisGenerationBusy}
            analysisQueuePending={analysisQueuePending}
            categories={categories}
            onAddCategory={onAddCategory}
            selectedFrames={selectedFrameNumbers}
            completedFrames={completedFrames}
            onToggleFrame={onToggleFrame}
            onSubmitFrameSelection={onSubmitFrameSelection}
            onClearFrameSelection={onClearFrameSelection}
            onSkipFrameSelection={onSkipFrameSelection}
            onFramesParsed={onFramesParsed}
            customReportsEnabled={customReportsEnabled}
            onUpdateCustomReport={customReportsEnabled ? onUpdateCustomReport : undefined}
            onDeleteCustomReport={customReportsEnabled ? onDeleteCustomReport : undefined}
            frameGroups={frameGroups}
            reportSections={reportSections}
            hasFrameBasedReports={hasFrameBasedReports}
            stepStage={stepStage}
            finalRegenerating={finalRegenerating}
            hideMicroreportLinks={isMarketResearch}
            allReports={[...microreports, ...customReports]}
            workspaceName={workspaceName}
            onNavigateToReport={(reportId) => {
              const resolvedId = microreportIdMap.get(reportId) ?? reportId;
              const target = microreports.find((r) => r.id === resolvedId);
              if (target) {
                onSelectReport(target);
              }
            }}
          />

          <div
            onMouseDown={(e) => { e.preventDefault(); onSetIsResizing(true); }}
            className={`w-1.5 cursor-col-resize bg-indigo-800/30 hover:bg-indigo-500/60 transition-colors ${
              isResizing ? 'bg-indigo-500/80' : ''
            }`}
            title="Drag to resize"
            style={{ userSelect: 'none' }}
          />

          <RightPanel
            sessionId={sessionId}
            agentId={agentId}
            scratchpadItems={scratchpadItems}
            onRemoveScratchpadItem={onRemoveScratchpadItem}
            onChatMessage={onChatMessage}
            onGenerateFraming={onGenerateFraming}
            isBusy={isChatBusy}
            width={rightPanelWidth}
            categories={categories}
            onAddCategory={onAddCategory}
            stepStatuses={stepStatuses}
            stepStage={stepStage}
            onCanvasCategoriesChange={onCanvasCategoriesChange}
            onCloseWorkspace={onCloseWorkspace}
            forceActiveTab={tourStep === 'scratchpad' ? 'scratch' : null}
            isClosingWorkspace={isClosingWorkspace}
          />
        </div>
        {showSessionNameModal && (
          <>
            <div className="session-name-modal-overlay" />
            <div className="session-name-modal">
              <div className="session-name-modal__panel">
                <h3>Name this workspace</h3>
                <p>Give this session a short label so you can find it later.</p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (sessionNameInput.trim() && !savingSessionName) {
                      onSaveSessionName();
                    }
                  }}
                >
                  <input
                    type="text"
                    value={sessionNameInput}
                    maxLength={80}
                    onChange={(e) => onSetSessionNameInput(e.target.value)}
                    placeholder="e.g., Vitamix NA blender launch"
                    autoFocus
                  />
                  <button
                    type="submit"
                    onClick={onSaveSessionName}
                    disabled={!sessionNameInput.trim() || savingSessionName}
                  >
                    {savingSessionName ? 'Saving…' : 'Save workspace name'}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
        {showCreditConfirmationModal && pendingGenerationMode && pendingFrameSelection && (
          <>
            <div className="credit-confirmation-modal-overlay" />
            <div className="credit-confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="credit-confirmation-title">
              <div className="credit-confirmation-modal__panel">
                <h3 id="credit-confirmation-title">Generate Analyses</h3>
                <div className="credit-confirmation-content">
                  {augmentationWorkerRunning ? (
                    <p className="credit-confirmation-text">
                      Updating analysis plan with your selected frames. Please wait before proceeding.
                    </p>
                  ) : isEstimatingGenerationCredits ? (
                    <p className="credit-confirmation-text">
                      Estimating credits...
                    </p>
                  ) : generationEstimateError ? (
                    <p className="credit-confirmation-text">
                      Unable to estimate credits right now. Please try again in a moment.
                    </p>
                  ) : generationEstimateStatus === 'updating' ? (
                    <p className="credit-confirmation-text">
                      {generationEstimateMessage || 'Updating analysis plan with your selected frames. Please wait before proceeding.'}
                    </p>
                  ) : generationEstimateStatus === 'not_ready' ? (
                    <p className="credit-confirmation-text">
                      {generationEstimateMessage || 'Analysis plan is not ready yet. Please wait a moment and try again.'}
                    </p>
                  ) : generationEstimateStatus === 'already_complete' && pendingGenerationMode === 'without_additional_frames' ? (
                    <p className="credit-confirmation-text">
                      {generationEstimateMessage || 'All selected analyses are already generated.'}
                    </p>
                  ) : (
                    (() => {
                      const additionalAnalyses = pendingFrameSelection.length;
                      const hasAdditionalFrames = pendingGenerationMode === 'with_additional_frames';
                      const includePresetAnalyses = !hasGeneratedPresetAnalyses;
                      const presetAnalysesCount = includePresetAnalyses ? presetAnalysesEstimate : 0;
                      const totalAnalysesEstimate = presetAnalysesCount + (hasAdditionalFrames ? additionalAnalyses : 0);
                      const insufficientCredits = typeof requiredCredits === 'number' && requiredCredits > credits;

                      if (insufficientCredits) {
                        return (
                          <p className="credit-confirmation-text" style={{ color: '#f87171' }}>
                            This analysis requires {totalAnalysesEstimate} credit{totalAnalysesEstimate === 1 ? '' : 's'} but you only have {credits}. Please contact your administrator to add credits.
                          </p>
                        );
                      }

                      return (
                        <p className="credit-confirmation-text">
                          astra estimates that this analysis will use up to {totalAnalysesEstimate} credit{totalAnalysesEstimate === 1 ? '' : 's'}. Do we proceed?
                        </p>
                      );
                    })()
                  )}
                </div>
                <div className="credit-confirmation-actions">
                  <button
                    type="button"
                    className="credit-confirmation-btn credit-confirmation-btn--cancel"
                    onClick={onCancelFrameSelection}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="credit-confirmation-btn credit-confirmation-btn--proceed"
                    onClick={onProceedWithFrameSelection}
                    disabled={
                      augmentationWorkerRunning ||
                      isEstimatingGenerationCredits ||
                      Boolean(generationEstimateError) ||
                      generationEstimateStatus === 'updating' ||
                      generationEstimateStatus === 'not_ready' ||
                      (generationEstimateStatus === 'already_complete' &&
                        pendingGenerationMode === 'without_additional_frames') ||
                      (typeof requiredCredits === 'number' && requiredCredits > credits)
                    }
                  >
                    {augmentationWorkerRunning || generationEstimateStatus === 'updating'
                      ? 'Updating...'
                      : isEstimatingGenerationCredits
                        ? 'Estimating...'
                        : 'Proceed'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* First-time tour message */}
      {showFirstTimeTour && (
        <div
          className="session-name-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="first-time-tour-title"
        >
          <div className="session-name-modal__panel" style={{ maxWidth: '500px', width: 'auto' }}>
            <h3 id="first-time-tour-title">Welcome to your workspace</h3>
            <p style={{ marginTop: '1rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Since this is your first time, I will give you a quick 1min tour of the most important features of your workspace.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'nowrap', marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer', flexShrink: 1, minWidth: 0 }}>
                <input
                  type="checkbox"
                  checked={firstTimeTourOptOut}
                  onChange={(e) => onSetFirstTimeTourOptOut(e.target.checked)}
                  style={{ 
                    flexShrink: 0, 
                    margin: 0, 
                    marginRight: '0.375rem',
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '0.875rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  Don't show this message again
                </span>
              </label>
              <button
                onClick={onDismissFirstTimeTour}
                style={{
                  padding: '0.625rem 0.625rem',
                  backgroundColor: '#06b6d4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.8125rem',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  width: 'auto',
                  minWidth: 'auto',
                }}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkspaceView;

